import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { SITE, scrollToId } from "@/lib/site";

const links = [
    { label: "About", id: "about" },
    { label: "Services", id: "services" },
    { label: "Why Us", id: "manifesto" },
    { label: "Contact", id: "contact" },
];

const scrollTo = scrollToId;

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "glass py-3" : "py-5 bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
                <button
                    onClick={() => scrollTo("hero")}
                    data-testid="logo-home-button"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={SITE.logo}
                        alt="Bhoir Group of Services logo"
                        className="h-11 w-11 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-[#F47B20] transition-all"
                    />
                    <div className="text-left leading-tight">
                        <span className="block font-display text-lg font-extrabold tracking-tight text-white">
                            BHOIR GROUP
                        </span>
                        <span className="block text-[10px] tracking-[0.3em] text-[#F47B20] font-semibold">
                            OF SERVICES
                        </span>
                    </div>
                </button>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-${l.id}`}
                            onClick={() => scrollTo(l.id)}
                            className="text-sm font-medium text-slate-300 hover:text-white relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-[#F47B20] after:transition-all hover:after:w-full"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <a
                        href={`tel:${SITE.phones[0]}`}
                        data-testid="header-call-link"
                        className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
                    >
                        <Phone size={15} /> {SITE.phones[0]} / {SITE.phones[1]}
                    </a>
                    <button
                        onClick={() => scrollTo("enquiry")}
                        data-testid="header-cta-button"
                        className="rounded-full bg-[#F47B20] px-5 py-2.5 text-sm font-semibold text-white orange-glow hover:bg-[#e06a10] hover:scale-105 transition-all"
                    >
                        Enquire Now
                    </button>
                </div>

                <button
                    className="md:hidden text-white"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Toggle menu"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden overflow-hidden glass mt-3 mx-4 rounded-2xl"
                    >
                        <div className="flex flex-col p-5 gap-4">
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-nav-${l.id}`}
                                    onClick={() => {
                                        scrollTo(l.id);
                                        setOpen(false);
                                    }}
                                    className="text-left text-slate-200 font-medium py-1"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                onClick={() => {
                                    scrollTo("enquiry");
                                    setOpen(false);
                                }}
                                data-testid="mobile-cta-button"
                                className="rounded-full bg-[#F47B20] px-5 py-3 text-sm font-semibold text-white"
                            >
                                Enquire Now
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
